import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./styles.module.css";
import { ModalProps } from "../../interface/form";

const Modal = ({
  isVisible = false,
  title,
  content,
  footer,
  onClose,
  size = "md",
  theme = "white",
}: ModalProps) => {
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const keydownHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    },
    [handleClose],
  );

  useEffect(() => {
    if (!isVisible) return;

    document.addEventListener("keydown", keydownHandler);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", keydownHandler);
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible, keydownHandler]);

  if (!isVisible) return null;

  return createPortal(
    <div className={styles.modal} role="presentation" onMouseDown={handleClose}>
      <section
        className={`${styles.modalDialog} ${styles[size]} ${styles[theme]}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3 id="modal-title" className={styles.modalTitle}>
            {title}
          </h3>

          <button
            type="button"
            className={`${styles.modalClose} ${styles[theme]}`}
            onClick={handleClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalContent}>{content}</div>
        </div>

        {footer && <div className={styles.modalFooter}>{footer}</div>}
      </section>
    </div>,
    document.body,
  );
};

export default Modal;
